const FORM_SELECTOR = '#registration-form';
const STATUS_SELECTOR = '#registration-status';
const TURNSTILE_CONTAINER_SELECTOR = '#registration-turnstile';
const TURNSTILE_TOKEN_SELECTOR = '#register-turnstile-token';
const ENDPOINT_ATTRIBUTE = 'data-registration-endpoint';
const TURNSTILE_SITE_KEY_ATTRIBUTE = 'data-turnstile-site-key';
const STATUS_CLASS_SUCCESS = 'register__status--success';
const STATUS_CLASS_ERROR = 'register__status--error';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let turnstileScriptPromise;

const UI_TEXTS = {
    cs: {
        submitting: 'Odesílám registraci...',
        success: 'Děkujeme, registrace byla odeslána.',
        error: 'Registraci se nepodařilo odeslat. Zkuste to prosím znovu.',
        missingEndpoint: 'Registrace není dostupná. Chybí bezpečný endpoint.',
        missingTurnstile: 'Registrace není dostupná. Chybí bezpečnostní ověření.',
        solveTurnstile: 'Nejdříve potvrďte bezpečnostní kontrolu.',
    },
    en: {
        submitting: 'Sending registration...',
        success: 'Thank you, your registration has been sent.',
        error: 'Registration could not be sent. Please try again.',
        missingEndpoint: 'Registration is unavailable. Secure endpoint is missing.',
        missingTurnstile: 'Registration is unavailable. Security verification is missing.',
        solveTurnstile: 'Please complete the security check first.',
    },
};

/**
 * Resolve localized UI text by page language.
 *
 * @return {Object}
 */
function getUiTexts() {
    const lang = document.documentElement.lang;

    if (lang === 'en') {
        return UI_TEXTS.en;
    }

    return UI_TEXTS.cs;
}

/**
 * Update status message shown below the registration form.
 *
 * @param {HTMLElement} statusElement
 * @param {string} message
 * @param {'success' | 'error'} type
 * @return {void}
 */
function setStatusMessage(statusElement, message, type) {
    statusElement.textContent = message;
    statusElement.classList.add('is-visible');
    statusElement.classList.remove(STATUS_CLASS_SUCCESS, STATUS_CLASS_ERROR);

    if (type === 'success') {
        statusElement.classList.add(STATUS_CLASS_SUCCESS);
        return;
    }

    statusElement.classList.add(STATUS_CLASS_ERROR);
}

/**
 * Toggle submitting state of the registration form.
 *
 * @param {HTMLFormElement} form
 * @param {boolean} isSubmitting
 * @return {void}
 */
function setSubmittingState(form, isSubmitting) {
    const submitButton = form.querySelector('button[type="submit"]');

    if (!submitButton) {
        return;
    }

    submitButton.disabled = isSubmitting;
    submitButton.setAttribute('aria-busy', String(isSubmitting));
}

/**
 * Load Cloudflare Turnstile script only once.
 *
 * @return {Promise<void>}
 */
function loadTurnstileScript() {
    if (turnstileScriptPromise) {
        return turnstileScriptPromise;
    }

    turnstileScriptPromise = new Promise(function buildTurnstilePromise(resolve, reject) {
        if (window.turnstile && typeof window.turnstile.render === 'function') {
            resolve();
            return;
        }

        const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);

        if (existingScript) {
            existingScript.addEventListener('load', function onLoad() {
                resolve();
            }, { once: true });
            existingScript.addEventListener('error', function onError() {
                reject(new Error('Turnstile script could not be loaded.'));
            }, { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', function onLoad() {
            resolve();
        }, { once: true });
        script.addEventListener('error', function onError() {
            reject(new Error('Turnstile script could not be loaded.'));
        }, { once: true });
        document.head.appendChild(script);
    });

    return turnstileScriptPromise;
}

/**
 * Initialize Turnstile challenge and keep token in hidden input.
 *
 * @param {HTMLFormElement} form
 * @param {string} siteKey
 * @return {Promise<boolean>}
 */
async function initTurnstile(form, siteKey) {
    const turnstileContainer = form.querySelector(TURNSTILE_CONTAINER_SELECTOR);
    const tokenInput = form.querySelector(TURNSTILE_TOKEN_SELECTOR);

    if (!turnstileContainer || !tokenInput) {
        console.error('Turnstile container or token field is missing.');
        return false;
    }

    await loadTurnstileScript();

    if (!window.turnstile || typeof window.turnstile.render !== 'function') {
        console.error('Turnstile API is not available.');
        return false;
    }

    window.turnstile.render(turnstileContainer, {
        sitekey: siteKey,
        callback: function onVerify(token) {
            tokenInput.value = token;
        },
        'expired-callback': function onExpire() {
            tokenInput.value = '';
        },
        'error-callback': function onError() {
            tokenInput.value = '';
        },
    });

    return true;
}

/**
 * Create payload from registration form values.
 *
 * @param {HTMLFormElement} form
 * @return {Object}
 */
function buildPayload(form) {
    const formData = new FormData(form);

    return {
        fullName: String(formData.get('fullName') || '').trim(),
        company: String(formData.get('company') || '').trim(),
        phone: String(formData.get('phone') || '').trim(),
        email: String(formData.get('email') || '').trim(),
        website: String(formData.get('website') || '').trim(),
        invitationCode: String(formData.get('invitationCode') || '').trim(),
        turnstileToken: String(formData.get('turnstileToken') || '').trim(),
        submittedAt: new Date().toISOString(),
        sourcePage: window.location.href,
        sourceLang: document.documentElement.lang || 'cs',
    };
}

/**
 * Submit registration payload to secure server endpoint.
 *
 * @param {string} endpoint
 * @param {Object} payload
 * @return {Promise<void>}
 */
async function submitRegistration(endpoint, payload) {
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error('Registration API request failed.');
    }
}

/**
 * Init registration form submission.
 *
 * @return {void}
 */
export function initRegistrationForm() {
    const form = document.querySelector(FORM_SELECTOR);

    if (!form) {
        return;
    }

    const statusElement = form.querySelector(STATUS_SELECTOR);

    if (!statusElement) {
        console.error('Registration form status element was not found.');
        return;
    }

    const uiTexts = getUiTexts();
    const endpoint = String(form.getAttribute(ENDPOINT_ATTRIBUTE) || '').trim();
    const turnstileSiteKey = String(form.getAttribute(TURNSTILE_SITE_KEY_ATTRIBUTE) || '').trim();

    if (!endpoint) {
        setStatusMessage(statusElement, uiTexts.missingEndpoint, 'error');
        return;
    }

    if (!turnstileSiteKey) {
        setStatusMessage(statusElement, uiTexts.missingTurnstile, 'error');
        return;
    }

    initTurnstile(form, turnstileSiteKey).catch(function onTurnstileInitError(error) {
        console.error('Failed to initialize Turnstile.', error);
        setStatusMessage(statusElement, uiTexts.missingTurnstile, 'error');
    });

    form.addEventListener('submit', async function onSubmit(event) {
        event.preventDefault();

        if (!form.reportValidity()) {
            return;
        }

        const payload = buildPayload(form);

        if (!payload.turnstileToken) {
            setStatusMessage(statusElement, uiTexts.solveTurnstile, 'error');
            return;
        }

        try {
            setSubmittingState(form, true);
            setStatusMessage(statusElement, uiTexts.submitting, 'success');
            await submitRegistration(endpoint, payload);
            setStatusMessage(statusElement, uiTexts.success, 'success');
            form.reset();

            if (window.turnstile && typeof window.turnstile.reset === 'function') {
                window.turnstile.reset();
            }
        } catch (error) {
            console.error('Failed to submit registration.', error);
            setStatusMessage(statusElement, uiTexts.error, 'error');
        } finally {
            setSubmittingState(form, false);
        }
    });
}
